import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { helpCategories } from "../help/helpData";
import HelpCenterLayout from "../help/HelpCenterLayout";
import Breadcrumbs from "../help/Breadcrumbs";
import ArticleContent from "../help/ArticleContent";
import TableOfContents from "../help/TableOfContents";
import "../../css/HelpCenter.css";

const HELP_BASE_PATH = "/help-archive";

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");

const HelpArchiveArticleDetails = () => {
  const { categoryId, articleId } = useParams();
  const contentRef = useRef(null);
  const [activeSection, setActiveSection] = useState("overview");

  const category = useMemo(
    () => helpCategories.find((item) => item.id === categoryId),
    [categoryId]
  );

  const article = useMemo(
    () => category?.articles.find((item) => item.id === articleId),
    [category, articleId]
  );

  const headings = useMemo(() => {
    if (!article?.content) return [];
    const list = [];
    const regex = /^(#{2,4})\s+(.+)$/gm;
    let match;

    while ((match = regex.exec(article.content)) !== null) {
      const level = match[1].length;
      const title = match[2].trim();
      list.push({
        id: slugify(title),
        title,
        level,
      });
    }

    return [{ id: "overview", title: "Overview", level: 2 }, ...list];
  }, [article?.content]);

  useEffect(() => {
    setActiveSection("overview");
  }, [articleId]);

  useEffect(() => {
    document.title = article
      ? `VelaKoda | ${article.title} Archive`
      : "VelaKoda | Help Center Archive";
  }, [article]);

  useEffect(() => {
    if (!contentRef.current) return;
    const headingElements = [
      document.getElementById("overview"),
      ...Array.from(contentRef.current.querySelectorAll("h2, h3, h4")),
    ].filter(Boolean);
    if (headingElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) =>
            a.boundingClientRect.top > b.boundingClientRect.top ? 1 : -1
          );
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        rootMargin: "-30% 0px -55% 0px",
        threshold: [0.1, 0.5],
      }
    );

    headingElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [article?.content]);

  const handleSectionClick = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
    }
  };

  if (!category || !article) {
    return (
      <HelpCenterLayout
        backFallbackTo={category ? `${HELP_BASE_PATH}/${category.id}` : HELP_BASE_PATH}
      >
        <Breadcrumbs
          items={[
            { label: "All Collections", to: HELP_BASE_PATH },
            category && {
              label: category.breadcrumbLabel,
              to: `${HELP_BASE_PATH}/${category.id}`,
            },
            { label: "Article not found", muted: true },
          ].filter(Boolean)}
        />
        <header className="help-page-header">
          <h1 className="help-page-title">Article not found</h1>
        </header>
        <div className="help-empty-state">
          Choose a different article or return to the Help Center Archive.
        </div>
      </HelpCenterLayout>
    );
  }

  return (
    <HelpCenterLayout backFallbackTo={`${HELP_BASE_PATH}/${category.id}`}>
      <Breadcrumbs
        items={[
          { label: "All Collections", to: HELP_BASE_PATH },
          { label: category.title, to: `${HELP_BASE_PATH}/${category.id}` },
          { label: article.title },
        ]}
      />

      <div className="help-article-layout">
        <article className="help-article-main">
          <header className="help-article-header" id="overview">
            <h1 className="help-article-title">{article.title}</h1>
            <p className="help-article-meta">Updated on {article.updatedAt}</p>
          </header>

          <div ref={contentRef}>
            <ArticleContent content={article.content} />
          </div>
        </article>

        {headings.length > 0 && (
          <TableOfContents
            headings={headings}
            activeSection={activeSection}
            onSelect={handleSectionClick}
          />
        )}
      </div>
    </HelpCenterLayout>
  );
};

export default HelpArchiveArticleDetails;
